import {
  Play,
  Pause,
  Square,
  SkipBack,
  Rewind,
  FastForward,
  Settings,
  Volume2,
  AlertTriangle,
} from 'lucide-react';
import { usePlaybackStore } from '@/stores';
import type { SkipContext } from '@/stores/playbackStore';
import type { PlaybackState } from '@/types';
import type { PianoLoadState } from '@/hooks/pianoLoadState';
import { PIANO_LOAD_ERROR_TEXT, formatPianoTraceText } from '@/utils/loadErrorText';
import { usePlaybackShortcuts } from '@/hooks/usePlaybackShortcuts';

/** OTO-027: fixed transport-skip amount for the ±5s buttons/shortcuts. */
const SKIP_SECONDS = 5;

interface PlaybackControlsProps {
  state: PlaybackState;
  totalDuration: number;
  tempo: number;
  onSettingsClick: () => void;
  audioLoaded: boolean;
  pianoLoadState: PianoLoadState;
  onPianoRetry: () => void;
  /** Latest X-Trace-ID for the active score (from upload/status-poll), shown on error */
  traceId?: string;
  /** OTO-027: resolved skip-clamp bounds (loop time bounds + totalDuration +
   *  chords), built by PracticeView from the score timeline -- the store
   *  has no timeline access to resolve config.loopStart/loopEnd (measure
   *  numbers) into seconds itself. */
  skipContext: SkipContext;
}

/**
 * Playback control bar with play/pause, seek, tempo, and settings
 */
export function PlaybackControls({
  state,
  totalDuration,
  tempo,
  onSettingsClick,
  audioLoaded,
  pianoLoadState,
  onPianoRetry,
  traceId,
  skipContext,
}: PlaybackControlsProps) {
  const play = usePlaybackStore((s) => s.play);
  const pause = usePlaybackStore((s) => s.pause);
  const stop = usePlaybackStore((s) => s.stop);
  const seek = usePlaybackStore((s) => s.seek);
  const skipBy = usePlaybackStore((s) => s.skipBy);
  const setTempoMultiplier = usePlaybackStore((s) => s.setTempoMultiplier);
  const toggleSustain = usePlaybackStore((s) => s.toggleSustain);
  const config = usePlaybackStore((state) => state.config);
  usePlaybackShortcuts({ skipContext, audioLoaded });
  // OTO-21: was a whole `position` object prop from PracticeView (60fps
  // churn); quantized to 0.25s here since this only drives the time
  // readout/seek-bar, not per-frame animation. Math.floor (not Math.round)
  // so the display never shows time ahead of actual playback position.
  const currentTime = usePlaybackStore((s) => Math.floor(s.position.currentTime * 4) / 4);

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Progress percentage
  const progress = totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0;

  // Handle seek on progress bar click
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * totalDuration;
    seek(newTime);
  };

  const handleTempoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempoMultiplier(parseFloat(e.target.value));
  };

  return (
    <div className="flex items-center gap-4">
      {/* Sustain toggle */}
      <button
        onClick={toggleSustain}
        className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded transition-colors ${
          config.sustain ? 'text-yellow-400' : 'text-gray-500 hover:text-gray-300'
        }`}
        title={config.sustain ? 'Sustain ON' : 'Sustain OFF'}
      >
        <svg viewBox="0 0 24 16" className="w-6 h-4" fill="currentColor">
          <rect x="2" y="0" width="3" height="16" rx="1" opacity={config.sustain ? 1 : 0.4} />
          <rect x="6" y="2" width="3" height="12" rx="1" opacity={config.sustain ? 1 : 0.4} />
          <rect x="10" y="0" width="3" height="16" rx="1" opacity={config.sustain ? 1 : 0.4} />
          <rect x="14" y="4" width="3" height="8" rx="1" opacity={config.sustain ? 1 : 0.4} />
          <rect x="18" y="2" width="3" height="12" rx="1" opacity={config.sustain ? 1 : 0.4} />
        </svg>
        <span className="text-[10px] leading-none font-medium">Sustain</span>
      </button>

      {/* Main controls */}
      <div className="flex items-center gap-2">
        {/* Stop button */}
        <button
          onClick={stop}
          className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          title="Stop"
        >
          <Square className="w-5 h-5" />
        </button>

        {/* Skip to start */}
        <button
          onClick={() => seek(0)}
          className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          title="Go to start"
        >
          <SkipBack className="w-5 h-5" />
        </button>

        {/* Skip back 5s */}
        <button
          onClick={() => skipBy(-SKIP_SECONDS, skipContext)}
          disabled={!audioLoaded}
          aria-label="Skip back 5 seconds"
          title="Skip back 5 seconds"
          className={`p-2 rounded-lg transition-colors ${
            audioLoaded ? 'hover:bg-gray-700' : 'cursor-not-allowed opacity-40'
          }`}
        >
          <Rewind className="w-5 h-5" />
        </button>

        {/* Play/Pause button */}
        <button
          onClick={() => (state === 'playing' ? pause() : play())}
          disabled={!audioLoaded}
          className={`p-3 rounded-full transition-colors ${
            audioLoaded ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 cursor-not-allowed'
          }`}
          title={state === 'playing' ? 'Pause' : 'Play'}
        >
          {state === 'playing' ? (
            <Pause className="w-6 h-6" />
          ) : (
            <Play className="w-6 h-6 ml-0.5" />
          )}
        </button>

        {/* Skip forward 5s */}
        <button
          onClick={() => skipBy(SKIP_SECONDS, skipContext)}
          disabled={!audioLoaded}
          aria-label="Skip forward 5 seconds"
          title="Skip forward 5 seconds"
          className={`p-2 rounded-lg transition-colors ${
            audioLoaded ? 'hover:bg-gray-700' : 'cursor-not-allowed opacity-40'
          }`}
        >
          <FastForward className="w-5 h-5" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="flex-1 flex items-center gap-3">
        <span className="text-sm text-gray-400 w-12 text-right">{formatTime(currentTime)}</span>

        <div
          className="flex-1 h-2 bg-gray-700 rounded-full cursor-pointer relative"
          onClick={handleSeek}
        >
          {/* Progress fill */}
          <div
            className="absolute top-0 left-0 h-full bg-blue-500 rounded-full"
            style={{ width: `${progress}%` }}
          />

          {/* Seek handle */}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg"
            style={{ left: `calc(${progress}% - 6px)` }}
          />

          {/* Loop markers if loop is set */}
          {config.loopStart !== null && config.loopEnd !== null && (
            <>
              <div
                className="absolute top-0 h-full bg-yellow-500/30"
                style={{
                  left: `${(config.loopStart / totalDuration) * 100}%`,
                  width: `${((config.loopEnd - config.loopStart) / totalDuration) * 100}%`,
                }}
              />
            </>
          )}
        </div>

        <span className="text-sm text-gray-400 w-12">{formatTime(totalDuration)}</span>
      </div>

      {/* Tempo slider */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-400 w-10 text-right">
          {Math.round(config.tempoMultiplier * 100)}%
        </span>
        <input
          type="range"
          min="0.25"
          max="2"
          step="0.05"
          value={config.tempoMultiplier}
          onChange={handleTempoChange}
          className="w-24 h-1.5 accent-blue-500 cursor-pointer"
          title={`${Math.round(tempo)} BPM`}
        />
        <span className="text-sm text-gray-500">{Math.round(tempo)} BPM</span>
      </div>

      {/* Wait mode indicator */}
      {state === 'waiting' && (
        <div className="flex items-center gap-2 text-yellow-400 text-sm">
          <span className="animate-pulse-slow">●</span>
          <span>Waiting for notes...</span>
        </div>
      )}

      {/* Audio loading indicator */}
      {pianoLoadState === 'loading' && (
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <Volume2 className="w-4 h-4 animate-pulse" />
          <span>Loading piano...</span>
        </div>
      )}

      {/* Audio load error + retry (Bug 2 -- OTO-18: was an infinite silent hang) */}
      {pianoLoadState === 'error' && (
        <div className="flex items-center gap-2 text-red-400 text-sm">
          <AlertTriangle className="w-4 h-4" />
          <span>{PIANO_LOAD_ERROR_TEXT}</span>
          {traceId && (
            <span className="text-xs text-red-300 font-mono select-all">
              {formatPianoTraceText(traceId)}
            </span>
          )}
          <button
            onClick={onPianoRetry}
            className="px-2 py-0.5 bg-red-900/50 hover:bg-red-900/80 rounded text-xs transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Settings button */}
      <button
        onClick={onSettingsClick}
        className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
        title="Settings"
      >
        <Settings className="w-5 h-5" />
      </button>
    </div>
  );
}
